import React, { useEffect, useState } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import AddIcon from "@material-ui/icons/Add";

import api from "../../../services/api";
import toastError from "../../../errors/toastError";
import { formatDateOnly } from "../helpers";
import NewOccurrenceModal from "./NewOccurrenceModal";
import { AttachmentIcon, formatDateTime } from "./helpers";
import StatusChip from "./StatusChip";
import ResolvedInfo from "./ResolvedInfo";
import FollowUpList from "./FollowUpList";
import FollowUpForm from "./FollowUpForm";
import ThirdPartyBox from "./ThirdPartyBox";

const useStyles = makeStyles((theme) => ({
  sectionTitle: {
    fontWeight: "bold",
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(0.5),
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5),
  },
  loadingBox: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(2),
  },
  occurrenceRow: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
  },
  occurrenceTop: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  occurrenceTitle: {
    fontWeight: "bold",
    flex: 1,
    minWidth: 160,
  },
  meta: {
    marginTop: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    fontSize: 12,
  },
  policyLink: {
    marginTop: theme.spacing(0.25),
    color: theme.palette.text.disabled,
    fontSize: 12,
    fontStyle: "italic",
  },
  description: {
    marginTop: theme.spacing(1),
    whiteSpace: "pre-wrap",
  },
  attachments: {
    marginTop: theme.spacing(1),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
  },
  attachmentLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    cursor: "pointer",
    color: theme.palette.primary.main,
    width: "fit-content",
    "&:hover": { textDecoration: "underline" },
  },
  emptySection: {
    fontSize: 13,
    color: theme.palette.text.disabled,
    padding: theme.spacing(0.5, 1),
  },
}));

/**
 * Seção "Ocorrências" do painel de detalhe da Carteira de Clientes. Substitui
 * a antiga página própria `pages/Occurrences` — a lista de ocorrências e o
 * modal de registro passam a viver dentro do expand de cada cliente, junto
 * de contato/endereço/apólices (decisão do dono do projeto).
 *
 * Busca POST /ocorrencias/cliente/consulta (documento vai no body, não mais
 * na URL, para não ficar gravado em access.log/error.log do Nginx) e separa
 * em duas subseções client-side pelo campo `vinculoVigente` que a API
 * já calcula: ocorrências vinculadas a apólice vigente (ou sem apólice
 * vinculada) vs. histórico de ocorrências cuja apólice vinculada já venceu.
 *
 * `policies` é `detail.vigentes` do ClientDetailPanel — repassado ao modal de
 * registro para a escolha de apólice quando houver mais de uma vigente.
 */
const OccurrencesSection = ({ documento, policies }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [occurrences, setOccurrences] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [openingAttachment, setOpeningAttachment] = useState(null); // `${id}-${index}` | null

  const fetchOccurrences = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/ocorrencias/cliente/consulta", { documento });
      setOccurrences(data.rows || []);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchOccurrences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documento]);

  const handleCreated = (created) => {
    setModalOpen(false);
    setOccurrences((previous) => [created, ...previous]);
  };

  const replaceOccurrence = (data) =>
    setOccurrences((previous) => previous.map((item) => (item.id === data.id ? data : item)));

  const handleChangeStatus = async (occurrence, status) => {
    setTogglingId(occurrence.id);
    try {
      const { data } = await api.patch(`/ocorrencias/${encodeURIComponent(occurrence.id)}/status`, { status });
      replaceOccurrence(data);
    } catch (err) {
      toastError(err);
    }
    setTogglingId(null);
  };

  /**
   * Abrir anexo em nova aba com token de curto prazo (2 min). Mesmo padrão
   * de ClientDetailPanel.openDocument: a aba é aberta ANTES do await para
   * não ser bloqueada por bloqueador de pop-up, a URL final só é setada
   * depois que o token chega, e `opener` é zerado.
   */
  const openAttachment = async (occurrence, attachment) => {
    const key = `${occurrence.id}-${attachment.index}`;
    const newTab = window.open("", "_blank");
    setOpeningAttachment(key);
    try {
      const { data } = await api.get(`/ocorrencias/${occurrence.id}/anexo/${attachment.index}/token`);
      const baseUrl = `${process.env.REACT_APP_BACKEND_URL}/ocorrencias/${occurrence.id}/anexo/${attachment.index}`;
      const url = `${baseUrl}?token=${encodeURIComponent(data.token)}`;
      if (newTab) {
        newTab.location.href = url;
        newTab.opener = null;
      } else {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      if (newTab) newTab.close();
      toastError(err);
    } finally {
      setOpeningAttachment(null);
    }
  };

  const renderOccurrence = (occurrence) => (
    <Paper key={occurrence.id} variant="outlined" className={classes.occurrenceRow}>
      <div className={classes.occurrenceTop}>
        <Typography variant="body2" className={classes.occurrenceTitle}>
          {occurrence.title}
        </Typography>
        <StatusChip
          status={occurrence.status}
          busy={togglingId === occurrence.id}
          onChange={(status) => (togglingId ? undefined : handleChangeStatus(occurrence, status))}
        />
      </div>

      <div className={classes.meta}>
        {formatDateTime(occurrence.createdAt)}
        {occurrence.createdByName ? ` · registrado por ${occurrence.createdByName}` : ""}
      </div>

      {/* Vínculo com apólice: só aparece quando existe (caso normal ausência
          de vínculo não é erro, não escrever nada em destaque nesse caso). */}
      <ResolvedInfo occurrence={occurrence} className={classes.meta} />

      {occurrence.policy ? (
        <div className={classes.policyLink}>
          {occurrence.policy.product || "—"} · {occurrence.policy.insurer || "—"} · vigência até{" "}
          {formatDateOnly(occurrence.policy.endDate)}
        </div>
      ) : null}

      {occurrence.description ? (
        <Typography variant="body2" className={classes.description}>
          {occurrence.description}
        </Typography>
      ) : null}

      {occurrence.attachments && occurrence.attachments.length > 0 ? (
        <div className={classes.attachments}>
          {occurrence.attachments.map((attachment) => {
            const key = `${occurrence.id}-${attachment.index}`;
            return (
              <div
                key={key}
                className={classes.attachmentLink}
                onClick={() => (openingAttachment ? undefined : openAttachment(occurrence, attachment))}
              >
                {openingAttachment === key ? (
                  <CircularProgress size={14} />
                ) : (
                  <AttachmentIcon mimeType={attachment.mimeType} fontSize="small" />
                )}
                <Typography variant="body2">{attachment.fileName}</Typography>
              </div>
            );
          })}
        </div>
      ) : null}

      <ThirdPartyBox occurrence={occurrence} onUpdated={replaceOccurrence} />

      <FollowUpList occurrenceId={occurrence.id} followUps={occurrence.followUps} />
      <FollowUpForm occurrenceId={occurrence.id} onSaved={replaceOccurrence} onReload={fetchOccurrences} />
    </Paper>
  );

  const vigentesOcorrencias = occurrences.filter((occurrence) => occurrence.vinculoVigente);
  const historicoOcorrencias = occurrences.filter((occurrence) => !occurrence.vinculoVigente);

  return (
    <>
      <div className={classes.headerRow}>
        <Typography variant="subtitle2" className={classes.sectionTitle} style={{ marginTop: 0 }}>
          Ocorrências (apólices vigentes)
        </Typography>
        <Button
          size="small"
          variant="outlined"
          color="primary"
          startIcon={<AddIcon fontSize="small" />}
          onClick={() => setModalOpen(true)}
        >
          Registrar Ocorrência
        </Button>
      </div>

      {loading ? (
        <div className={classes.loadingBox}>
          <CircularProgress size={24} />
        </div>
      ) : (
        <>
          {vigentesOcorrencias.length ? (
            vigentesOcorrencias.map(renderOccurrence)
          ) : (
            <div className={classes.emptySection}>Nenhuma ocorrência vinculada a apólice vigente.</div>
          )}

          <Typography variant="subtitle2" className={classes.sectionTitle}>
            Ocorrências (histórico — apólices vencidas)
          </Typography>
          {historicoOcorrencias.length ? (
            historicoOcorrencias.map(renderOccurrence)
          ) : (
            <div className={classes.emptySection}>Nenhuma ocorrência no histórico.</div>
          )}
        </>
      )}

      <NewOccurrenceModal
        open={modalOpen}
        cpfCnpj={documento}
        policies={policies}
        onClose={() => setModalOpen(false)}
        onCreated={handleCreated}
      />
    </>
  );
};

export default OccurrencesSection;
